import { SpsListenTyp } from './sps-listen-typ.model';
import { SpsImportEintrag } from './sps-import.model';

/*
 * Spiegelbild der Backend-DTOs aus Models_SpsDtos.cs (Abschnitte SPS-Signale
 * und Prüfung, Signalverknüpfung). Enums kommen als Klartext.
 */

// =========================================================
// ENUMS (Models_Sps.cs)
// =========================================================

export type SpsSignalRichtung = 'Unbekannt' | 'Eingang' | 'Ausgang';

/** Genau ein Prüfergebnis je Signal. */
export type SpsPruefstatus = 'Ungeprueft' | 'Ok' | 'NichtOk' | 'Reserve';

/** Ungeklaert = die Verknüpfung Eintrag ↔ Signal wartet auf eine Entscheidung. */
export type SpsZuordnungsStatus = 'Ungeklaert' | 'Bestaetigt';

/** Entscheidung beim Zusammenführen zweier bereits geprüfter Signale. */
export type SpsKonfliktLoesung = 'Keine' | 'ZielBehalten' | 'QuelleUebernehmen';

export const SPS_PRUEFSTATUS_WERTE: SpsPruefstatus[] = ['Ungeprueft', 'Ok', 'NichtOk', 'Reserve'];

export const SPS_RICHTUNG_WERTE: SpsSignalRichtung[] = ['Eingang', 'Ausgang', 'Unbekannt'];

/** Anzeige eines Prüfstatus: Text, Icon und CSS-Klasse (Farbe) – überall gleich. */
export interface SpsPruefstatusOption {
  wert: SpsPruefstatus;
  text: string;
  icon: string;
  /** CSS-Klasse: status-ungeprueft | status-ok | status-nok | status-reserve */
  klasse: string;
}

export const SPS_PRUEFSTATUS_OPTIONEN: SpsPruefstatusOption[] = [
  { wert: 'Ungeprueft', text: 'Ungeprüft', icon: 'radio_button_unchecked', klasse: 'status-ungeprueft' },
  { wert: 'Ok', text: 'OK', icon: 'check_circle', klasse: 'status-ok' },
  { wert: 'NichtOk', text: 'Nicht OK', icon: 'cancel', klasse: 'status-nok' },
  { wert: 'Reserve', text: 'Reserve', icon: 'pause_circle', klasse: 'status-reserve' }
];

export function pruefstatusOption(status: SpsPruefstatus | null | undefined): SpsPruefstatusOption {
  return SPS_PRUEFSTATUS_OPTIONEN.find(o => o.wert === status) ?? SPS_PRUEFSTATUS_OPTIONEN[0];
}

export function pruefstatusText(status: SpsPruefstatus | null | undefined): string {
  return pruefstatusOption(status).text;
}

export function richtungText(richtung: SpsSignalRichtung | null | undefined): string {
  switch (richtung) {
    case 'Eingang': return 'Eingang';
    case 'Ausgang': return 'Ausgang';
    default: return 'Unbekannt';
  }
}

/** Vorheriges/nächstes Signal in derselben Filterung und Sortierung (GET api/sps/signale/{id}/nachbarn). */
export interface SpsSignalNachbarn {
  signalId: number;
  /** 1-basierte Position in der gefilterten Menge; 0, wenn das Signal nicht (mehr) zum Filter passt. */
  position: number;
  gesamt: number;
  vorherigesId: number | null;
  naechstesId: number | null;
}


// =========================================================
// SIGNALE  (GET api/sps/signale, GET api/sps/signale/{id})
// =========================================================

export interface SpsSignal {
  id: number;
  projektId: number;
  name: string | null;
  adresseNormiert: string | null;
  adresseOriginal: string | null;
  stationsSchluessel: string | null;
  beschreibung: string | null;
  bmk: string | null;
  datenTyp: string | null;
  richtung: SpsSignalRichtung;
  pruefstatus: SpsPruefstatus;
  bemerkung: string | null;
  zuletztGeprueftAmUtc: string | null;
  zuletztGeprueftVon: string | null;
  /** Für optimistische Nebenläufigkeit – beim Statuswechsel mitsenden. */
  version: number;
  quellenAnzahl: number;
  hatUngeklaerteZuordnung: boolean;
  /** Aus welchen Listentypen das Signal stammt. */
  quellenTypen: SpsListenTyp[];
  /** True, wenn sich die Quellen (Zuordnungsliste ↔ PLC-Tags) in Name, Beschreibung, Station, BMK oder Datentyp widersprechen. */
  hatAbweichendeQuellen: boolean;
  /**
   * Ein Listenupdate hat prüfrelevante Angaben einer Quelle dieses geprüften Signals
   * verändert („Seit Prüfung geändert“); der Prüfstatus wurde NICHT zurückgesetzt.
   */
  geaendertSeitPruefungAmUtc?: string | null;
  geaendertSeitPruefungHinweis?: string | null;
  /** Anlagenteile der Quellen (aus Ort bzw. Path), ohne Dubletten. */
  anlagenteilNamen?: string[];
  /**
   * Der EINE Anlagenteil des Signals: genau ein Anlagenteil über alle bestätigten
   * Quellen. Null, wenn keine bestätigte Quelle einen trägt oder die Quellen sich
   * widersprechen (dann stehen alle Namen in `anlagenteilNamen`).
   */
  anlagenteilId?: number | null;
  anlagenteilName?: string | null;
}

/** Ein Feld, in dem sich die Quellen eines Signals unterscheiden – mit Herkunft je Wert. */
export interface SpsSignalUnterschied {
  feld: string;
  werte: { quelle: string; wert: string }[];
}

export interface SpsSignalQuelle {
  eintragId: number;
  listeId: number;
  listeName: string;
  listeTyp: SpsListenTyp;
  excelZeile: number;
  status: SpsZuordnungsStatus;
  zuordnungsGrund: string | null;
  verknuepftAmUtc: string;
  eintrag: SpsImportEintrag;
}

export interface SpsPruefprotokoll {
  id: number;
  signalId: number;
  alterStatus: SpsPruefstatus;
  neuerStatus: SpsPruefstatus;
  bemerkung: string | null;
  geprueftVon: string | null;
  geprueftAmUtc: string;
}

export interface SpsSignalDetail extends SpsSignal {
  quellen: SpsSignalQuelle[];
  /** Die letzten (höchstens 10) Protokolleinträge, neueste zuerst. */
  letzteProtokolle: SpsPruefprotokoll[];
  /** Widersprüchliche Angaben der Quellen; die vollständigen Originalzeilen stehen in `quellen`. */
  unterschiede: SpsSignalUnterschied[];
}


// =========================================================
// PRÜFSTATUS  (PUT api/sps/signale/{id}/status)
// =========================================================

export interface SpsPruefstatusRequest {
  status: SpsPruefstatus;
  /** Bei NichtOk Pflicht. */
  bemerkung?: string | null;
  /** Version aus dem zuletzt geladenen Signal; weicht sie ab → HTTP 409. */
  version?: number | null;
}


// =========================================================
// FORTSCHRITT  (GET api/sps/fortschritt)
// =========================================================

/**
 * Bearbeitet = Ok + NichtOk + Reserve (Reserve zählt als bearbeitet).
 * Pruefbar = Gesamt − Reserve. ErfolgProzent = Ok / (Ok + NichtOk), ohne Reserve.
 */
export interface SpsFortschritt {
  gesamt: number;
  ungeprueft: number;
  ok: number;
  nichtOk: number;
  reserve: number;
  bearbeitet: number;
  pruefbar: number;
  bearbeitetProzent: number;
  erfolgProzent: number;
  eingaenge: number;
  ausgaenge: number;
  richtungUnbekannt: number;
  ungeklaert: number;
}

export function leererSpsFortschritt(): SpsFortschritt {
  return {
    gesamt: 0, ungeprueft: 0, ok: 0, nichtOk: 0, reserve: 0,
    bearbeitet: 0, pruefbar: 0, bearbeitetProzent: 0, erfolgProzent: 0,
    eingaenge: 0, ausgaenge: 0, richtungUnbekannt: 0, ungeklaert: 0
  };
}

/** SPS-Fortschritt EINES Anlagenteils – dieselbe Rechnung wie `SpsFortschritt`, nur auf dessen Signale beschränkt. */
export interface SpsAnlagenteilFortschritt {
  anlagenteilId: number;
  name: string;
  sortOrder: number;
  fortschritt: SpsFortschritt;
}

/**
 * SPS-Fortschritt je Anlagenteil eines Projekts (ProjektDetailDto.SpsAnlagenteile).
 * Jedes prüfbare Signal zählt genau einmal: bei genau einem Anlagenteil über
 * seine bestätigten Quellen in dessen Gruppe, sonst unter „ohne“ bzw. „mehrdeutig“.
 */
export interface SpsAnlagenteilUebersicht {
  anlagenteile: SpsAnlagenteilFortschritt[];
  signaleGesamt: number;
  signaleOhneAnlagenteil: number;
  signaleMehrdeutig: number;
}

export function leereSpsAnlagenteilUebersicht(): SpsAnlagenteilUebersicht {
  return { anlagenteile: [], signaleGesamt: 0, signaleOhneAnlagenteil: 0, signaleMehrdeutig: 0 };
}


// =========================================================
// ANLAGENTEIL-FILTEROPTIONEN (listen- bzw. prüfungsbezogen)
// =========================================================

/**
 * Eine Option des Anlagenteil-FILTERS: ein Anlagenteil, der in der gerade
 * geöffneten SPS-Liste bzw. in der SPS-Prüfung tatsächlich vorkommt – genau
 * die Werte, für die der Filter `anlagenteilId` Zeilen bzw. Signale liefert.
 */
export interface SpsAnlagenteilOption {
  anlagenteilId: number;
  name: string;
  sortOrder: number;
  /** Zeilen der Liste bzw. Signale der Prüfung mit diesem Anlagenteil (über alle Seiten). */
  anzahl: number;
}

/**
 * Filteroptionen EINER SPS-Liste (GET api/sps/listen/{id}/anlagenteile) bzw.
 * der SPS-Prüfung eines Projekts (GET api/sps/anlagenteile?projektId=): nur
 * verwendete Anlagenteile, ohne Dubletten, alphabetisch, über alle Zeilen bzw.
 * Signale und nicht nur die geladene Seite. Bewusst getrennt von der
 * Zuordnungsauswahl, die alle Anlagenteile des Projekts zeigt.
 */
export interface SpsAnlagenteilOptionen {
  anlagenteile: SpsAnlagenteilOption[];
  /** Zeilen bzw. Signale ohne Anlagenteil – nur dann gibt es die Option „Ohne Anlagenteil“. */
  ohneAnlagenteil: number;
  gesamt: number;
}

export function leereSpsAnlagenteilOptionen(): SpsAnlagenteilOptionen {
  return { anlagenteile: [], ohneAnlagenteil: 0, gesamt: 0 };
}


// =========================================================
// FILTER  (Query-Parameter von GET api/sps/signale)
// =========================================================

/** Sortierfelder des Backends; Suffix `_desc` für absteigend. */
export type SpsSignalSortierung =
  | 'adresse' | 'adresse_desc'
  | 'name' | 'name_desc'
  | 'status' | 'status_desc'
  | 'station' | 'station_desc'
  | 'geprueft' | 'geprueft_desc';

export interface SpsSignalFilter {
  projektId: number;
  richtung?: SpsSignalRichtung | null;
  status?: SpsPruefstatus | null;
  /** Volltext über Name, Adresse, Beschreibung, Station, BMK, Symbol. */
  suche?: string | null;
  name?: string | null;
  adresse?: string | null;
  bmk?: string | null;
  beschreibung?: string | null;
  station?: string | null;
  nurUngeklaert?: boolean | null;
  /** Ungeprüft oder NichtOk. */
  nurOffen?: boolean | null;
  /** Nur geprüfte Signale, deren Quellen sich seit der Prüfung durch ein Listenupdate geändert haben. */
  nurGeaendertSeitPruefung?: boolean | null;
  /** Nur Signale, bei denen eine bestätigte Quelle diesen Anlagenteil (Id des Projekts) trägt. */
  anlagenteilId?: number | null;
  /** Nur Signale ohne Anlagenteil an irgendeiner Quelle. Hat Vorrang vor `anlagenteilId`. */
  ohneAnlagenteil?: boolean | null;
  /** 1-basiert. */
  seite?: number;
  /** Höchstens 500. */
  groesse?: number;
  sortierung?: SpsSignalSortierung | null;
}


// =========================================================
// SIGNALVERKNÜPFUNG  (api/sps/zuordnung/…)
// =========================================================

export interface SpsZuordnungKandidat {
  signalId: number;
  name: string | null;
  adresseNormiert: string | null;
  stationsSchluessel: string | null;
  richtung: SpsSignalRichtung;
  pruefstatus: SpsPruefstatus;
  quellenAnzahl: number;
  quellenTypen: SpsListenTyp[];
  grund: string;
  /** True, wenn der Kandidat nach der Signalidentität dasselbe physische Signal ist. */
  vertraeglich: boolean;
}

/** Eine ungeklärte Quelle: Eintrag, sein (vorläufiges) Signal und die Kandidaten. */
export interface SpsUngeklaerteZuordnung {
  signalId: number;
  eintragId: number;
  listeId: number;
  listeName: string;
  listeTyp: SpsListenTyp;
  excelZeile: number;
  signalname: string | null;
  adresseOriginal: string | null;
  adresseNormiert: string | null;
  richtung: SpsSignalRichtung;
  stationsName: string | null;
  bmk: string | null;
  beschreibung: string | null;
  grund: string | null;
  eigenerPruefstatus: SpsPruefstatus;
  kandidaten: SpsZuordnungKandidat[];
}

export interface SpsZuordnungBestaetigenRequest {
  eintragId: number;
  zielSignalId: number;
  /** Pflicht, wenn beide Signale bereits geprüft sind – sonst antwortet das Backend mit 409. */
  konfliktloesung?: SpsKonfliktLoesung;
}

export interface SpsZuordnungEigenstaendigRequest {
  eintragId: number;
}

export interface SpsZuordnungAutomatischErgebnis {
  geprueft: number;
  verknuepft: number;
  weiterhinUngeklaert: number;
}


// =========================================================
// BEREINIGUNG  (api/sps/bereinigung/…, api/sps/signale/zusammenfuehren)
// =========================================================

export type SpsBereinigungKonfliktArt = 'Pruefstatus' | 'Mehrdeutig' | 'Plausibilitaet' | 'VerwaistGeprueft';

export type SpsBereinigungTrennungsArt = 'AndereStation' | 'ManuellGetrennt';

export interface SpsBereinigungQuelle {
  eintragId: number;
  listeId: number;
  listeName: string;
  listeTyp: SpsListenTyp;
  excelZeile: number;
  signalname: string | null;
  stationsName: string | null;
  adresseOriginal: string | null;
  beschreibung: string | null;
  bmk: string | null;
  status: SpsZuordnungsStatus;
  /** Warum der Import diese Zeile so zugeordnet hat – erklärt, weshalb ein Duplikat entstand. */
  zuordnungsGrund: string | null;
}

export interface SpsBereinigungSignal {
  id: number;
  name: string | null;
  adresseNormiert: string | null;
  stationsSchluessel: string | null;
  beschreibung: string | null;
  richtung: SpsSignalRichtung;
  pruefstatus: SpsPruefstatus;
  bemerkung: string | null;
  zuletztGeprueftAmUtc: string | null;
  zuletztGeprueftVon: string | null;
  protokollAnzahl: number;
  quellen: SpsBereinigungQuelle[];
}

/** Eine sichere Zusammenführung: die Quellsignale gehen im Zielsignal auf. */
export interface SpsZusammenfuehrung {
  zielSignalId: number;
  quellSignalIds: number[];
  adresseNormiert: string | null;
  richtung: SpsSignalRichtung;
  grund: string;
  /** Anzeigename des Zielsignals nach der Zusammenführung (sprechender Name vor Adressname). */
  ergebnisName: string | null;
  ergebnisPruefstatus: SpsPruefstatus;
  statusUebernommenVon: number | null;
  unterschiede: SpsSignalUnterschied[];
  signale: SpsBereinigungSignal[];
}

/** Ein Fall, der eine manuelle Entscheidung verlangt. */
export interface SpsBereinigungKonflikt {
  art: SpsBereinigungKonfliktArt;
  adresseNormiert: string | null;
  richtung: SpsSignalRichtung;
  grund: string;
  hinweis: string;
  signalIds: number[];
  signale: SpsBereinigungSignal[];
}

export interface SpsBereinigungTrennung {
  art: SpsBereinigungTrennungsArt;
  adresseNormiert: string | null;
  richtung: SpsSignalRichtung;
  grund: string;
  signalIds: number[];
}

export interface SpsBereinigungKlaerung {
  signalId: number;
  eintragId: number;
  adresseNormiert: string | null;
  grund: string;
}

/** Dry-Run-Bericht (GET api/sps/bereinigung/bericht) – ändert nichts. */
export interface SpsBereinigungBericht {
  projektId: number;
  erstelltAmUtc: string;
  dryRun: boolean;
  signaleVorher: number;
  signaleNachher: number;
  anzahlZusammenfuehrungen: number;
  anzahlSignaleAufgehend: number;
  anzahlVerwaisteSignale: number;
  anzahlKlaerungen: number;
  anzahlKonflikte: number;
  anzahlTrennungen: number;
  anzahlNamensanpassungen: number;
  zusammenfuehrungen: SpsZusammenfuehrung[];
  verwaisteSignale: SpsBereinigungSignal[];
  klaerungen: SpsBereinigungKlaerung[];
  konflikte: SpsBereinigungKonflikt[];
  trennungen: SpsBereinigungTrennung[];
  /** Signale, deren Anzeigename vom Adressnamen („I0.0“) auf den sprechenden Namen einer Quelle („07-BMA“) wechselt. */
  namensanpassungen: SpsBereinigungNamensanpassung[];
  hinweise: string[];
}

export interface SpsBereinigungNamensanpassung {
  signalId: number;
  adresseNormiert: string | null;
  bisherigerName: string | null;
  neuerName: string | null;
  grund: string;
}

/** Ergebnis von POST api/sps/bereinigung/anwenden?bestaetigen=true. */
export interface SpsBereinigungErgebnis {
  projektId: number;
  zusammenfuehrungen: number;
  signaleAufgegangen: number;
  verwaisteGeloescht: number;
  klaerungen: number;
  namensanpassungen: number;
  verbleibendeKonflikte: number;
  signaleVorher: number;
  signaleNachher: number;
  bericht: SpsBereinigungBericht;
}

/** Ein Projekt in der Bestandsbereinigung (GET/POST api/sps/bereinigung/altbestand…, nur SuperAdmin). */
export interface SpsAltbestandProjekt {
  projektId: number;
  projektName: string;
  projektNummer: string | null;
  signaleVorher: number;
  signaleNachher: number;
  zusammenfuehrungen: number;
  signaleAufgehend: number;
  verwaisteSignale: number;
  klaerungen: number;
  namensanpassungen: number;
  konflikte: number;
  trennungen: number;
  nichtsZuTun: boolean;
  fehler: string | null;
  angewendet: boolean;
}

/** Dry-Run der Bestandsbereinigung über alle Projekte mit SPS-Daten. Ändert nichts. */
export interface SpsAltbestandBericht {
  erstelltAmUtc: string;
  dryRun: boolean;
  projekte: number;
  projekteMitHandlungsbedarf: number;
  zusammenfuehrungen: number;
  signaleAufgehend: number;
  verwaisteSignale: number;
  klaerungen: number;
  namensanpassungen: number;
  konflikte: number;
  projektBerichte: SpsAltbestandProjekt[];
  hinweise: string[];
}

/** Ergebnis der angewendeten Bestandsbereinigung (eine Transaktion je Projekt). */
export interface SpsAltbestandErgebnis {
  ausgefuehrtAmUtc: string;
  projekte: number;
  projekteAngewendet: number;
  projekteFehlgeschlagen: number;
  zusammenfuehrungen: number;
  signaleAufgegangen: number;
  verwaisteGeloescht: number;
  klaerungen: number;
  namensanpassungen: number;
  verbleibendeKonflikte: number;
  projektErgebnisse: SpsAltbestandProjekt[];
  hinweise: string[];
}

export interface SpsSignaleZusammenfuehrenRequest {
  zielSignalId: number;
  quellSignalIds: number[];
  /** Pflicht, wenn Ziel und Quelle beide geprüft und verschieden sind – sonst 409. */
  konfliktloesung?: SpsKonfliktLoesung;
}
